import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, ShieldAlert } from 'lucide-react';
import CreditApplicationForm from './components/CreditApplicationForm';
import W9Form from './components/W9Form';
import CreditCardAuthorizationForm from './components/CreditCardAuthorizationForm';
import RateAgreementLetterForm from './components/RateAgreementLetterForm';
import type { PublicInviteData } from './types';

interface PublicFormRunnerProps {
  inviteId: string;
}

export default function PublicFormRunner({ inviteId }: PublicFormRunnerProps) {
  const [invite, setInvite] = useState<PublicInviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!inviteId) {
      setError('Invalid application link.');
      setLoading(false);
      return;
    }

    let isMounted = true;

    async function loadInvite() {
      try {
        const res = await fetch(`/api/public/forms/${inviteId}`);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Form link is invalid or has expired.');
        }

        const data: PublicInviteData = await res.json();
        if (isMounted) {
          setInvite(data);
          setLoading(false);

          // Fire open event
          fetch(`/api/public/forms/${inviteId}/open`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          }).catch(() => {});
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.message || 'Failed to load form.');
          setLoading(false);
        }
      }
    }

    loadInvite();

    return () => {
      isMounted = false;
    };
  }, [inviteId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors">
        <div className="text-center space-y-4">
          <Loader2 className="animate-spin text-blue-600 mx-auto" size={44} />
          <p className="text-slate-600 dark:text-slate-400 font-medium text-sm">
            Loading secure Armellini form...
          </p>
        </div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 transition-colors">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 p-10 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 text-center space-y-5">
          <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert size={36} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Form Link Inactive
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            {error || 'This form link has expired, been revoked, or is invalid.'}
          </p>
          <div className="pt-2 text-xs text-slate-400 dark:text-slate-500">
            Please contact your Armellini Logistics account representative to request a new link.
          </div>
        </div>
      </div>
    );
  }

  // Render the appropriate form
  switch (invite.formType) {
    case 'credit_application':
      return <CreditApplicationForm invite={invite} />;
    case 'w9':
      return <W9Form invite={invite} />;
    case 'credit_card_authorization':
      return <CreditCardAuthorizationForm invite={invite} />;
    case 'rate_agreement_letter':
      return <RateAgreementLetterForm invite={invite} />;
    default:
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-900 p-8 rounded-2xl shadow text-center">
            <AlertCircle size={36} className="text-amber-500 mx-auto mb-3" />
            <p className="text-slate-700 dark:text-slate-300">
              Unsupported form type: {(invite as any).formType}
            </p>
          </div>
        </div>
      );
  }
}
